import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter } from "react-router";
import { RegionProvider } from "./contexts/RegionContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import AppRouter from "./router/AppRouter";
import { ErrorBoundary } from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import { Toaster } from "sonner";

const LoadingSpinner = () => (
  <div className="flex-center min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
    <div className="text-center space-y-4">
      <div className="loading-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
      <p style={{ color: 'var(--text-secondary)' }}>Loading TrainZilla...</p>
    </div>
  </div>
);

export default function App() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        console.log("🚀 Initializing TrainZilla Marketing Site...");

        // Analytics: the GA4 base tag (G-9Q8E40LN2K) is configured in index.html
        // and route-change page views are handled by GA4 Enhanced Measurement.
        // Nothing to re-configure here — the previous call passed a literal
        // "GA_MEASUREMENT_ID" placeholder and registered a phantom stream.

        setIsInitialized(true);
      } catch (error) {
        setIsInitialized(true);
      }
    };
    initializeApp();
  }, []);

  if (!isInitialized) {
    return <LoadingSpinner />;
  }

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ThemeProvider>
          <RegionProvider>
            <LanguageProvider>
              {/* No global <SEOHead />: every route renders its own <SEOPageHead>
                  (or <SEOHead>). A second app-level Helmet instance raced the
                  page one, so prerendered pages sometimes got the homepage
                  <title>/canonical. Site-wide Organization JSON-LD lives in
                  index.html. */}
              <ScrollToTop />

              <div className="size-full min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
                <Suspense fallback={<LoadingSpinner />}>
                  <AppRouter />
                  
                  {!isOnline && (
                    <div className="fixed bottom-4 left-4 z-50 bg-white border border-orange-300 rounded-xl p-3 shadow-lg">
                      <div className="flex items-center gap-2 text-orange-700">
                        <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium">Offline Mode</span>
                      </div>
                    </div>
                  )}
                </Suspense>
              </div>

              <Toaster
                position="top-right"
                expand={true}
                richColors={true}
                closeButton={true}
              />
            </LanguageProvider>
          </RegionProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}